import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Play, X } from 'lucide-react';

interface WelcomeVideoProps {
  className?: string;
  backgroundVideoUrl?: string;
  youtubeVideoId?: string;
  title?: string;
  description?: string;
}

const WelcomeVideo: React.FC<WelcomeVideoProps> = ({
  className = '',
  backgroundVideoUrl = 'https://d1tg5gdu842vo5.cloudfront.net/site-assets/video/manufacturing_website_compressed.webm',
  youtubeVideoId = 'Mx3PJw8OKtI',
  title = 'Welcome to Kibu',
  description = 'Discover how we\'re revolutionising children\'s headphones with sustainable, repairable design that kids build themselves.'
}) => {
  const [showLightbox, setShowLightbox] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleWatchClick = () => {
    console.log('Watch button clicked');
    setShowLightbox(true);
    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden';
  };

  const handleCloseLightbox = () => {
    console.log('Closing lightbox');
    setShowLightbox(false);
    // Restore body scroll
    document.body.style.overflow = '';
  };

  // console.log('showLightbox:', showLightbox);

  return (
    <>
      <div className="module_container">
        <section
          className={`relative w-full h-[600px] md:h-[500px] flex items-center justify-center overflow-hidden rounded-3xl ${className}`}
        >
          {/* Video Background - webm */}
          <video
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src={backgroundVideoUrl} type="video/webm" />
          </video>

          {/* Dark overlay for better text contrast */}
          <div className="absolute inset-0 bg-black/40"></div>

          {/* Content */}
          <div className="relative z-10 text-center px-4 md:px-8 max-w-2xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-4 leading-tight">
              {title}
            </h2>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-8">
              {description}
            </p>

            {/* Watch Video Button */}
            <button
              onClick={handleWatchClick}
              className="inline-flex items-center gap-3 px-8 py-4 bg-green text-white rounded-lg font-headline text-lg hover:bg-yellow-400 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              <Play className="w-6 h-6" fill="currentColor" />
              Watch Video
            </button>
          </div>
        </section>
      </div>

      {/* YouTube Lightbox - Using Portal to render at document root */}
      {mounted && showLightbox && createPortal(
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center p-4"
          style={{ zIndex: 99999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={handleCloseLightbox}
        >
          <div className="relative w-full max-w-5xl" style={{ aspectRatio: '16/9' }} onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={handleCloseLightbox}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors z-10"
              aria-label="Close video"
            >
              <X className="w-8 h-8" />
            </button>

            {/* YouTube Embed */}
            <iframe
              className="w-full h-full rounded-2xl"
              src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default WelcomeVideo;
